import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { 
  Search, 
  Filter, 
  Download, 
  Plus, 
  Calendar, 
  Upload 
} from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/components/ui/use-toast";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

import { SubsidyAllocation, getAllSubsidyAllocations, saveSubsidyAllocation } from "@/lib/db/subsidyDb";

const SubsidyAllocationTab: React.FC = () => {
  const [allocations, setAllocations] = useState<SubsidyAllocation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterSubsidyType, setFilterSubsidyType] = useState<string>("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { toast } = useToast();
  
  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm({
    defaultValues: {
      customerName: "",
      icNumber: "",
      memberType: "Customer",
      incomeRange: "",
      subsidyAmount: 0,
      subsidyType: "Monthly",
      eligibleCategories: [] as string[],
      notes: "",
      expiryDate: new Date(),
    }
  });
  
  useEffect(() => {
    const loadAllocations = async () => {
      setIsLoading(true);
      try {
        const data = await getAllSubsidyAllocations();
        setAllocations(data);
        setIsLoading(false);
      } catch (error) {
        console.error("Error loading subsidy allocations:", error);
        toast({
          variant: "destructive",
          title: "Failed to load allocations",
          description: "Could not retrieve subsidy allocations. Please try again."
        });
        setIsLoading(false);
      }
    };
    
    loadAllocations();
  }, [toast]);
  
  const onSubmitAllocation = async (data: any) => {
    try {
      // Generate a unique ID for the new allocation
      const allocationId = `sa_${Date.now().toString()}`;
      
      // Ensure eligibleCategories is always an array
      const eligibleCategories = Array.isArray(data.eligibleCategories)
        ? data.eligibleCategories
        : data.eligibleCategories ? [data.eligibleCategories] : [];
      
      const newAllocation: SubsidyAllocation = {
        allocationId,
        customerName: data.customerName,
        customerId: `cust_${Date.now()}`, // This would normally come from customer selection
        icNumber: data.icNumber,
        memberType: data.memberType,
        incomeRange: data.incomeRange,
        subsidyAmount: parseFloat(data.subsidyAmount),
        subsidyType: data.subsidyType,
        eligibleCategories: eligibleCategories,
        allocationDate: new Date(),
        expiryDate: new Date(data.expiryDate),
        assignedBy: "Current User", // Would come from auth context in real app
        notes: data.notes,
        status: "Active"
      };
      
      await saveSubsidyAllocation(newAllocation);
      setAllocations(prev => [...prev, newAllocation]);
      
      toast({
        title: "Subsidy Allocated",
        description: `Subsidy has been successfully allocated to ${data.customerName}.`
      });
      
      reset();
      setIsDialogOpen(false);
    } catch (error) {
      console.error("Error assigning subsidy:", error);
      toast({
        variant: "destructive",
        title: "Failed to allocate subsidy",
        description: "An error occurred while allocating the subsidy. Please try again."
      });
    }
  };
  
  const filteredAllocations = allocations
    .filter(allocation => {
      // Apply search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          allocation.customerName.toLowerCase().includes(searchLower) ||
          allocation.icNumber.toLowerCase().includes(searchLower)
        );
      }
      return true;
    })
    .filter(allocation => {
      // Apply status filter
      if (filterStatus !== "all") {
        return allocation.status.toLowerCase() === filterStatus.toLowerCase();
      }
      return true;
    })
    .filter(allocation => {
      // Apply subsidy type filter
      if (filterSubsidyType !== "all") {
        return allocation.subsidyType.toLowerCase() === filterSubsidyType.toLowerCase();
      }
      return true;
    });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle>Subsidy Allocations</CardTitle>
              <CardDescription>
                View and manage subsidy allocations for eligible beneficiaries.
              </CardDescription>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="whitespace-nowrap">
                    <Plus className="mr-2 h-4 w-4" />
                    New Allocation
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[525px]">
                  <DialogHeader>
                    <DialogTitle>Assign New Subsidy</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit(onSubmitAllocation)} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="customerName">Customer Name</Label>
                        <Input
                          id="customerName"
                          placeholder="Enter customer name"
                          {...register("customerName", { required: true })}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="icNumber">IC Number</Label>
                        <Input
                          id="icNumber"
                          placeholder="Enter IC number"
                          {...register("icNumber", { required: true })}
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="memberType">Member Type</Label>
                        <Select onValueChange={(value) => setValue("memberType", value)} defaultValue="Customer">
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select member type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Customer">Customer</SelectItem>
                            <SelectItem value="Coop Member">Coop Member</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="incomeRange">Income Range</Label>
                        <Select onValueChange={(value) => setValue("incomeRange", value)}>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select income range" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Below RM1000">Below RM1000</SelectItem>
                            <SelectItem value="RM1000-RM2000">RM1000-RM2000</SelectItem>
                            <SelectItem value="RM2000-RM3000">RM2000-RM3000</SelectItem>
                            <SelectItem value="RM3000-RM4000">RM3000-RM4000</SelectItem>
                            <SelectItem value="Above RM4000">Above RM4000</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="subsidyAmount">Subsidy Amount (RM)</Label>
                        <Input
                          id="subsidyAmount"
                          type="number"
                          min="1"
                          step="1"
                          placeholder="Enter amount"
                          {...register("subsidyAmount", { valueAsNumber: true, required: true })}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="subsidyType">Subsidy Type</Label>
                        <Select onValueChange={(value) => setValue("subsidyType", value)} defaultValue="Monthly">
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select subsidy type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Monthly">Monthly</SelectItem>
                            <SelectItem value="One-Time">One-Time</SelectItem>
                            <SelectItem value="Conditional">Conditional</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="eligibleCategories">Eligible Product Categories</Label>
                      <Select onValueChange={(value) => setValue("eligibleCategories", [value])}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select eligible categories" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Food Essentials">Food Essentials</SelectItem>
                          <SelectItem value="Education">Education</SelectItem>
                          <SelectItem value="Healthcare">Healthcare</SelectItem>
                          <SelectItem value="Household">Household</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="expiryDate">Expiry Date</Label>
                      <Input
                        id="expiryDate"
                        type="date"
                        {...register("expiryDate", { required: true })}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="notes">Notes</Label>
                      <Textarea
                        id="notes"
                        placeholder="Additional notes or comments"
                        className="min-h-[80px]"
                        {...register("notes")}
                      />
                    </div>
                    
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit">
                        Assign Subsidy
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              <Button variant="outline">
                <Upload className="mr-2 h-4 w-4" />
                Bulk Upload
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or IC number..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                    <SelectItem value="used">Used</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <Select value={filterSubsidyType} onValueChange={setFilterSubsidyType}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="one-time">One-Time</SelectItem>
                    <SelectItem value="conditional">Conditional</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" title="Export data">
                <Download className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : (
            filteredAllocations.length === 0 ? (
              <div className="text-center py-10">
                <p className="text-muted-foreground">No subsidy allocations found. Create a new allocation to get started.</p>
              </div>
            ) : (
              <div className="rounded-lg border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer Name</TableHead>
                      <TableHead>IC Number</TableHead>
                      <TableHead>Member Type</TableHead>
                      <TableHead>Amount (RM)</TableHead>
                      <TableHead>Subsidy Type</TableHead>
                      <TableHead>Eligible Categories</TableHead>
                      <TableHead>Expiry Date</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAllocations.map((allocation) => (
                      <TableRow key={allocation.allocationId}>
                        <TableCell>{allocation.customerName}</TableCell>
                        <TableCell>{allocation.icNumber}</TableCell>
                        <TableCell>{allocation.memberType}</TableCell>
                        <TableCell>{allocation.subsidyAmount.toFixed(2)}</TableCell>
                        <TableCell>{allocation.subsidyType}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {allocation.eligibleCategories.map((category) => (
                              <Badge key={category} variant="outline" className="whitespace-nowrap">
                                {category}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>{format(new Date(allocation.expiryDate), "dd/MM/yyyy")}</TableCell>
                        <TableCell>
                          <Badge variant={
                            allocation.status === "Active" ? "default" :
                            allocation.status === "Expired" ? "destructive" :
                            allocation.status === "Used" ? "secondary" : "outline"
                          }>
                            {allocation.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SubsidyAllocationTab;
